import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Crown, ArrowRight } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { listingUrl, TYPE_META } from '@/lib/seo';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTO_MS = 5000;

const nice = (s) => (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const HeadlineCarousel = () => {
  const [items, setItems] = useState([]);
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/headline-businesses`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(d.items || []))
      .catch(() => {});
  }, []);

  const onSelect = useCallback(() => {
    if (api) setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    api.on('select', onSelect);
    const t = setInterval(() => api.scrollNext(), AUTO_MS);
    return () => { clearInterval(t); api.off('select', onSelect); };
  }, [api, onSelect]);

  if (!items.length) return null;

  return (
    <section className="bg-gray-950 py-12 sm:py-14 overflow-hidden" data-testid="headline-carousel">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-2">
              <Crown className="w-4 h-4" /> Headline Partners
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">This week's spotlight businesses</h2>
          </div>
          <span className="shrink-0 px-2.5 py-1 rounded-full bg-white/10 text-white/60 text-[10px] font-semibold uppercase tracking-wider" data-testid="headline-sponsored-label">
            Sponsored
          </span>
        </div>

        <Carousel setApi={setApi} opts={{ loop: true, align: 'start' }} className="relative">
          <CarouselContent>
            {items.map((b) => (
              <CarouselItem key={b.slug || b.id} className="md:basis-1/2 lg:basis-1/3">
                <div className="gold-frame rounded-2xl p-[3px]" data-testid={`headline-gold-frame-${b.slug}`}>
                  <Link
                    to={listingUrl(b.type, b.slug)}
                    className="group block relative rounded-[13px] overflow-hidden h-72 bg-gray-900"
                    data-testid={`headline-card-${b.slug}`}
                  >
                    {b.image && (
                      <img
                        src={b.image}
                        alt={b.title || b.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10" />
                    {/* Luxury gold corner ornaments — sponsors only */}
                    <span aria-hidden className="gold-corner top-3 left-3 border-t-2 border-l-2 rounded-tl-md" />
                    <span aria-hidden className="gold-corner top-3 right-3 border-t-2 border-r-2 rounded-tr-md" />
                    <span aria-hidden className="gold-corner bottom-3 left-3 border-b-2 border-l-2 rounded-bl-md" />
                    <span aria-hidden className="gold-corner bottom-3 right-3 border-b-2 border-r-2 rounded-br-md" />
                    <span className="gold-shine-sweep" aria-hidden />
                    <div className="absolute top-5 left-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#f7e08b] via-[#e6c766] to-[#c9a227] text-gray-900 text-[10px] font-bold uppercase tracking-wider shadow-[0_1px_8px_rgba(201,162,39,0.7)]">
                      <Crown className="w-3 h-3" /> Gold Partner
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                      <div className="text-[11px] text-amber-200/90 mb-1">
                        {(TYPE_META[b.type] || {}).emoji} {(TYPE_META[b.type] || {}).label || nice(b.type)} · <MapPin className="inline w-3 h-3 -mt-0.5" /> {nice(b.city)}
                      </div>
                      <h3 className="font-heading font-bold text-xl leading-tight">{b.title || b.name}</h3>
                      <div className="flex items-center gap-3 mt-2 text-sm">
                        <span className="inline-flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {b.rating}
                          <span className="text-white/60 text-xs">({(b.reviews_count || 0).toLocaleString()})</span>
                        </span>
                        {b.price && <span className="text-white/70 text-xs">{b.price}</span>}
                        <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity">
                          Explore <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {items.length > 1 && (
            <>
              <CarouselPrevious className="hidden sm:flex -left-4 bg-white/10 border-white/20 text-white hover:bg-white/25 hover:text-white" data-testid="headline-prev" />
              <CarouselNext className="hidden sm:flex -right-4 bg-white/10 border-white/20 text-white hover:bg-white/25 hover:text-white" data-testid="headline-next" />
            </>
          )}
        </Carousel>

        {items.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-5" data-testid="headline-dots">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => api?.scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={cn('h-1.5 rounded-full transition-all',
                  i === current ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/25 hover:bg-white/50')}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
